/**
 * 비밀번호 재설정 완료 — 메일 링크의 ?token=... 파라미터로 접근.
 *
 * 새 비밀번호 + 확인 입력 → 검증 → 백엔드 적용 → 로그인 화면으로 안내.
 */

import { useState, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ApiCallError } from '../lib/api';
import * as authApi from '../lib/auth-api';

export function PasswordResetConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (pw1.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다');
      return;
    }
    if (pw1 !== pw2) {
      setError('두 비밀번호가 일치하지 않습니다');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.confirmPasswordReset({ token, newPassword: pw1 });
      setDone(true);
      // 3초 후 로그인 화면으로
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (e) {
      const msg = e instanceof ApiCallError ? e.message : '재설정 실패';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-full max-w-sm bg-white rounded shadow-sm border border-slate-200 p-8 text-center">
          <div className="text-3xl mb-2">⚠️</div>
          <h1 className="text-lg font-bold mb-2">잘못된 링크</h1>
          <p className="text-sm text-slate-600 mb-4">
            메일에 포함된 링크 전체를 사용해주세요.
          </p>
          <Link to="/login" className="text-sm text-blue-600 hover:underline">
            로그인 화면으로
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-full max-w-sm bg-white rounded shadow-sm border border-slate-200 p-8 text-center">
          <div className="text-3xl mb-2">✅</div>
          <h1 className="text-lg font-bold mb-2">비밀번호 변경 완료</h1>
          <p className="text-sm text-slate-600">
            잠시 후 로그인 화면으로 이동합니다…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-white rounded shadow-sm border border-slate-200 p-8"
      >
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold">새 비밀번호 설정</h1>
          <p className="mt-1 text-xs text-slate-500">
            최소 8자 이상으로 설정하세요
          </p>
        </div>

        <label className="block mb-3">
          <span className="text-sm text-slate-700">새 비밀번호</span>
          <input
            type="password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
            minLength={8}
            className="ui-input mt-1 w-full"
          />
        </label>

        <label className="block mb-3">
          <span className="text-sm text-slate-700">새 비밀번호 (확인)</span>
          <input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            className="ui-input mt-1 w-full"
          />
        </label>

        {error && (
          <div className="mb-3 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary w-full"
        >
          {submitting ? '변경 중…' : '비밀번호 변경'}
        </button>
      </form>
    </div>
  );
}
